import React, { Fragment, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight, Download, RotateCcw, Search, Bookmark, Inbox, Loader } from 'react-feather';

import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

import useDatagridQuery from './useDatagridQuery';
import DateRangePresets from './DateRangePresets';
import KpiStrip from './KpiStrip';
import './datagrid.scss';

const VIEW_LS_KEY = (mode) => `dg.views.${mode}`;
const PAGE_SIZE_OPTIONS = [100, 250, 500];

const DataGridShell = ({ mode, endpoint, exportEndpoint, columns: baseColumns, kpiAccumulators, preFetch }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authorized = localStorage.getItem('accessDataGrid') === '1';
    const gridApiRef = useRef(null);
    const filterDebounceRef = useRef(null);
    const quickSearchDebounceRef = useRef(null);

    const [extraColumns, setExtraColumns] = useState([]);
    const [quickSearch, setQuickSearch] = useState('');
    const [viewMenuOpen, setViewMenuOpen] = useState(false);
    const [savedViews, setSavedViews] = useState(() => {
        try { return JSON.parse(localStorage.getItem(VIEW_LS_KEY(mode)) || '{}'); }
        catch { return {}; }
    });

    const query = useDatagridQuery({ mode, endpoint, exportEndpoint });

    useEffect(() => {
        if (!authorized) {
            toast.error(t('You are not authorized to view Data Grid'));
            navigate('/dashboard/default', { replace: true });
        }
    }, [authorized, navigate, t]);

    useEffect(() => {
        if (preFetch) {
            preFetch().then(cols => setExtraColumns(cols || [])).catch(() => setExtraColumns([]));
        }
    }, [preFetch]);

    const columnDefs = useMemo(
        () => [...baseColumns, ...extraColumns].map(c => (c.headerName ? { ...c, headerName: t(c.headerName) } : c)),
        [baseColumns, extraColumns, t]
    );

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        filterParams: { debounceMs: 300, suppressAndOrCondition: true, caseSensitive: false }
    }), []);

    const columnTypes = useMemo(() => ({
        number: { cellClass: 'ag-right-aligned-cell', filter: 'agNumberColumnFilter' }
    }), []);

    const gridRestoredRef = useRef(false);
    const suppressGridEventsRef = useRef(false);

    const onGridReady = (p) => {
        gridApiRef.current = p.api;
        const hasFilter = query.filterModelRef.current && Object.keys(query.filterModelRef.current).length > 0;
        const hasSort = query.sortModelRef.current && query.sortModelRef.current.length > 0;
        if (hasFilter || hasSort) {
            suppressGridEventsRef.current = true;
            if (hasFilter) p.api.setFilterModel(query.filterModelRef.current);
            if (hasSort) {
                p.api.applyColumnState({
                    state: query.sortModelRef.current.map(s => ({ colId: s.colId, sort: s.sort })),
                    defaultState: { sort: null }
                });
            }
            setTimeout(() => { suppressGridEventsRef.current = false; gridRestoredRef.current = true; }, 50);
        } else {
            gridRestoredRef.current = true;
        }
    };

    const filterModelEqual = (a, b) => {
        const ak = Object.keys(a || {}).sort();
        const bk = Object.keys(b || {}).sort();
        if (ak.length !== bk.length) return false;
        return ak.every((k, i) => k === bk[i] && JSON.stringify(a[k]) === JSON.stringify(b[k]));
    };
    const sortModelEqual = (a, b) => {
        if ((a || []).length !== (b || []).length) return false;
        return (a || []).every((s, i) => s.colId === b[i].colId && s.sort === b[i].sort);
    };

    const onFilterChanged = () => {
        if (!gridApiRef.current || suppressGridEventsRef.current || !gridRestoredRef.current) return;
        const fm = gridApiRef.current.getFilterModel() || {};
        if (filterModelEqual(fm, query.filterModelRef.current)) return;
        query.setFilter(fm);
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => query.fetchPage(1), 350);
    };

    const onSortChanged = () => {
        if (!gridApiRef.current || suppressGridEventsRef.current || !gridRestoredRef.current) return;
        const sm = gridApiRef.current.getColumnState()
            .filter(c => c.sort)
            .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
            .map(c => ({ colId: c.colId, sort: c.sort }));
        if (sortModelEqual(sm, query.sortModelRef.current)) return;
        query.setSort(sm);
        query.fetchPage(1);
    };

    const onQuickSearch = (val) => {
        setQuickSearch(val);
        clearTimeout(quickSearchDebounceRef.current);
        quickSearchDebounceRef.current = setTimeout(() => {
            if (gridApiRef.current && gridApiRef.current.setGridOption) {
                gridApiRef.current.setGridOption('quickFilterText', val);
            } else if (gridApiRef.current && gridApiRef.current.setQuickFilter) {
                gridApiRef.current.setQuickFilter(val);
            }
        }, 200);
    };

    const handleReset = () => {
        if (gridApiRef.current) {
            gridApiRef.current.setFilterModel(null);
            gridApiRef.current.applyColumnState({ defaultState: { sort: null } });
        }
        setQuickSearch('');
        query.reset();
    };

    const persistViews = useCallback((next) => {
        setSavedViews(next);
        try { localStorage.setItem(VIEW_LS_KEY(mode), JSON.stringify(next)); } catch { }
    }, [mode]);

    const saveCurrentView = () => {
        if (!gridApiRef.current) return;
        const name = window.prompt(`${t('Name this view')}:`);
        if (!name) return;
        const state = gridApiRef.current.getColumnState();
        persistViews({ ...savedViews, [name]: state });
        toast.success(t('Saved view "{{name}}"', { name }));
    };

    const applyView = (name) => {
        if (!gridApiRef.current) return;
        const state = savedViews[name];
        if (!state) return;
        gridApiRef.current.applyColumnState({ state, applyOrder: true });
        setViewMenuOpen(false);
        toast.info(t('Applied view "{{name}}"', { name }));
    };

    const deleteView = (name) => {
        const next = { ...savedViews };
        delete next[name];
        persistViews(next);
    };

    const totalPages = Math.max(1, Math.ceil(query.totalCount / query.pageSize));
    const canPrev = query.page > 1;
    const canNext = query.page < totalPages;
    const firstRow = query.totalCount === 0 ? 0 : (query.page - 1) * query.pageSize + 1;
    const lastRow = Math.min(query.page * query.pageSize, query.totalCount);

    const [pageInput, setPageInput] = useState(query.page);
    useEffect(() => setPageInput(query.page), [query.page]);
    const onJump = (e) => {
        e.preventDefault();
        const n = Math.min(Math.max(1, parseInt(pageInput, 10) || 1), totalPages);
        query.fetchPage(n);
    };

    if (!authorized) return null;

    const filtersActive = Object.keys(query.filterModelRef.current || {}).length > 0;
    const showEmpty = !query.loading && query.hasLoaded && query.rows.length === 0;
    const showInitial = !query.loading && !query.hasLoaded && query.rows.length === 0;

    return (
        <Fragment>
            <div className="v3 dg-page">
                <div className="dg-toolbar v3-card">
                    <div className="dg-toolbar-row dg-toolbar-row-top">
                        <DateRangePresets
                            preset={query.preset}
                            startDate={query.startDate}
                            endDate={query.endDate}
                            onPreset={query.applyPreset}
                            onCustom={query.setCustomDates}
                        />
                        <div className="dg-quick-search">
                            <Search size={14} />
                            <input
                                type="text"
                                placeholder={t('Quick search on this page…')}
                                value={quickSearch}
                                onChange={e => onQuickSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="dg-toolbar-row dg-toolbar-row-bottom">
                        <div className="dg-actions">
                            <button
                                className="v3-btn v3-btn-primary"
                                onClick={() => query.fetchPage(1)}
                                disabled={query.loading}
                            >
                                {query.loading ? <Loader size={14} className="dg-spin" /> : null}
                                {query.loading ? t('Loading…') : t('Load Data')}
                            </button>
                            <button
                                className="v3-btn"
                                onClick={query.exportCsv}
                                disabled={query.exporting || query.totalCount === 0}
                                title={t('Export filtered + sorted result to CSV')}
                            >
                                <Download size={14} />
                                {query.exporting ? t('Exporting…') : t('Export CSV')}
                            </button>
                            <div className="dg-view-menu">
                                <button
                                    className="v3-btn v3-btn-ghost"
                                    onClick={() => setViewMenuOpen(o => !o)}
                                >
                                    <Bookmark size={14} />
                                    {t('Views')}
                                </button>
                                {viewMenuOpen && (
                                    <div className="dg-view-dropdown" onMouseLeave={() => setViewMenuOpen(false)}>
                                        <button className="dg-view-item dg-view-save" onClick={saveCurrentView}>
                                            + {t('Save current view')}
                                        </button>
                                        {Object.keys(savedViews).length === 0 && (
                                            <div className="dg-view-empty">{t('No saved views')}</div>
                                        )}
                                        {Object.keys(savedViews).map(name => (
                                            <div className="dg-view-item" key={name}>
                                                <span onClick={() => applyView(name)}>{name}</span>
                                                <button className="dg-view-del" onClick={() => deleteView(name)}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button className="v3-btn v3-btn-ghost" onClick={handleReset}>
                                <RotateCcw size={14} />
                                {t('Reset')}
                            </button>
                        </div>
                    </div>
                </div>

                <KpiStrip rows={query.rows} totalCount={query.totalCount} accumulators={kpiAccumulators} />

                <div className="dg-grid-wrap">
                    <div className="ag-theme-quartz dg-grid">
                        <AgGridReact
                            rowData={query.rows}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            columnTypes={columnTypes}
                            onGridReady={onGridReady}
                            onFilterChanged={onFilterChanged}
                            onSortChanged={onSortChanged}
                            pagination={false}
                            animateRows={true}
                            suppressMenuHide={true}
                            multiSortKey="ctrl"
                            loadingOverlayComponent={undefined}
                            overlayLoadingTemplate={`<span class="dg-overlay">${t('Loading rows…')}</span>`}
                            overlayNoRowsTemplate={`<span class="dg-overlay">${t('No rows')}</span>`}
                        />
                    </div>

                    {showInitial && (
                        <div className="dg-empty">
                            <Inbox size={36} />
                            <h4>{t('Pick a date range and load data')}</h4>
                            <p>{t('Use a preset above or choose Custom, then hit')} <b>{t('Load Data')}</b>.</p>
                        </div>
                    )}
                    {showEmpty && (
                        <div className="dg-empty">
                            <Inbox size={36} />
                            <h4>{filtersActive ? t('No rows match these filters') : t('No data in this range')}</h4>
                            <p>{filtersActive ? t('Try clearing column filters or widening the date range.') : t('Try a different preset or widen the date range.')}</p>
                            {filtersActive && (
                                <button className="v3-btn v3-btn-ghost" onClick={handleReset}>{t('Clear filters')}</button>
                            )}
                        </div>
                    )}
                </div>

                <div className="dg-pagination">
                    <div className="dg-pagination-info">
                        {t('Showing')} <b>{firstRow}</b>–<b>{lastRow}</b> {t('of')} <b>{query.totalCount.toLocaleString()}</b>
                    </div>
                    <div className="dg-pagination-controls">
                        <label className="dg-page-size">
                            {t('Rows')}
                            <select
                                value={query.pageSize}
                                onChange={e => query.changePageSize(parseInt(e.target.value, 10))}
                            >
                                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                        <button
                            className="dg-page-btn"
                            onClick={() => query.fetchPage(query.page - 1)}
                            disabled={!canPrev}
                            aria-label={t('Previous page')}
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <form className="dg-page-jump" onSubmit={onJump}>
                            <input
                                type="number"
                                min="1"
                                max={totalPages}
                                value={pageInput}
                                onChange={e => setPageInput(e.target.value)}
                            />
                            <span>/ {totalPages}</span>
                        </form>
                        <button
                            className="dg-page-btn"
                            onClick={() => query.fetchPage(query.page + 1)}
                            disabled={!canNext}
                            aria-label={t('Next page')}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default DataGridShell;
